import { additionDisplayName } from 'constants/GlobalConfig';

/**
 * 段階選択式（type=10）の選択肢スコアを序列設定から算出する。
 * 悪い方の選択肢から 1, 2, … と付与し、最大値は選択肢数となる。
 * @param index 選択肢の並び順（0始まり）
 * @param total 選択肢数
 * @param positive true=選択肢1が良い回答（選択肢1が最大スコア） / false=選択肢1が悪い回答
 */
export const candidateScoreOf = (index: number, total: number, positive: boolean): number =>
    positive ? total - index : index + 1;

/**
 * 段階選択式（type=10）の選択肢スコアから序列設定（選択肢1が良い回答か）を復元する。
 * スコア未設定（他の回答形式）の場合は既定の true を返す。
 */
export const isCandidateOrderPositive = (
    candidates: { order: number; score?: number | null }[]
): boolean => {
    const sorted = [...candidates].sort((a, b) => a.order - b.order);
    const first = sorted[0]?.score;
    const last = sorted[sorted.length - 1]?.score;
    if (first === undefined || first === null || last === undefined || last === null) return true;
    return first >= last;
};

const convertCandidate = (
    candidate: Http.Connection.Response.EverydayNote.Candidate
): EverydayNote.Candidate => {
    return {
        content: candidate.content,
        order: candidate.order,
        uuid: candidate.uuid,
        score: candidate.score,
    };
};

const convertQuestion = (
    question: Http.Connection.Response.EverydayNote.Question
): EverydayNote.Question => {
    const newCandidates = question.candidates.map(convertCandidate);

    return {
        candidates: newCandidates,
        content: question.content,
        detail: question.detail,
        evaluationScaleMax: question.evaluation_scale_max,
        isRequired: question.is_required,
        order: question.order,
        type: question.type,
        uuid: question.uuid,
        sliderRangeMax: question.slider_range_max,
        sliderRangeMin: question.slider_range_min,
        sliderInitialUserValue: question.slider_initial_user_value,
        showSliderValueDetails: question.show_slider_value_details,
        sliderRangeMinDetail: question.slider_range_min_detail,
        sliderRangeMaxDetail: question.slider_range_max_detail,
        sliderInitialUserValueDetail: question.slider_initial_user_value_detail,
        markDetails: question.mark_details,
        candidateOrderPositive: isCandidateOrderPositive(question.candidates),
    };
};

/**
 * バックエンドからのレスポンスをフロントエンドで使うフォーマットに変換する
 */
export const convertQuestionnaire = (
    questionnaire: PartOfPartial<
        Http.Connection.Response.EverydayNote.Questionnaire,
        'target_grades' | 'target_organization' | 'target_permissions' | 'target_term'
    >
): EverydayNote.ControlQuestionnaire => {
    const createdBy = {
        displayName: additionDisplayName(questionnaire.created_by).display_name,
    };
    const updatedBy = {
        displayName: additionDisplayName(questionnaire.updated_by).display_name,
    };

    const newQuestions = questionnaire.items.map(convertQuestion);

    return {
        afterMessage: questionnaire.after_message,
        allowPastDateAnswer: questionnaire.allow_past_date_answer,
        canAdminSetPublic: questionnaire.can_admin_set_public,
        canComment: questionnaire.can_comment,
        createdAt: questionnaire.created_at,
        createdBy: createdBy,
        description: questionnaire.description,
        explanation: questionnaire.explanation,
        isDesignatedTerm: questionnaire.is_designated_term,
        isHolidayAnswerDisallowed: questionnaire.is_holiday_answer_disallowed,
        isLocked: questionnaire.is_locked,
        isOnceADay: questionnaire.is_once_a_day,
        isRequired: questionnaire.is_required,
        isViewedHome: questionnaire.is_viewed_home,
        items: newQuestions,
        name: questionnaire.name,
        publishEndAt: questionnaire.publish_end_at,
        publishFrom: questionnaire.publish_from,
        publishStartAt: questionnaire.publish_start_at,
        publishToAll: questionnaire.publish_to_all,
        recordableDayOfWeeks: questionnaire.day_of_weeks,
        recordableTimeEnd: questionnaire.end_time,
        recordableTimeStart: questionnaire.start_time,
        statusPeriod: questionnaire.status_period,
        targetGrades: questionnaire.target_grades?.map(e => e.code) ?? [],
        targetOrganizations: questionnaire?.target_organization ?? [],
        targetPermissions: questionnaire.target_permissions?.map(e => e.code) ?? [],
        targetTerm: questionnaire.target_term?.uuid ?? undefined,
        type: questionnaire.type,
        updatedAt: questionnaire.updated_at,
        updatedBy: updatedBy,
        uuid: questionnaire.uuid,
        files: questionnaire.files
    };
};


const convertAdminCandidate = (
    candidate: Http.Connection.Response.EverydayNote.QuestionAdmin['candidates'][number]
): EverydayNote.Candidate => {
    return {
        content: candidate.content,
        order: candidate.order,
        uuid: candidate.uuid,
        score: candidate.score,
    };
};

const convertAdminQuestion = (
    question: Http.Connection.Response.EverydayNote.QuestionAdmin
): EverydayNote.Question => {
    const newCandidates = question.candidates.map(convertAdminCandidate);

    return {
        candidates: newCandidates,
        content: question.content,
        detail: question.detail,
        evaluationScaleMax: question.evaluation_scale_max,
        isRequired: question.is_required,
        order: question.order,
        type: question.type,
        uuid: question.uuid,
        sliderRangeMax: question.slider_range_max,
        sliderRangeMin: question.slider_range_min,
        sliderInitialUserValue: question.slider_initial_user_value,
        showSliderValueDetails: question.show_slider_value_details,
        sliderRangeMinDetail: question.slider_range_min_detail,
        sliderRangeMaxDetail: question.slider_range_max_detail,
        sliderInitialUserValueDetail: question.slider_initial_user_value_detail,
        markDetails: question.mark_details,
        candidateOrderPositive: isCandidateOrderPositive(question.candidates),
    };
};

/**
 * バックエンドからのレスポンスをフロントエンドで使うフォーマットに変換する
 */
export const convertAdminQuestionnaire = (
    questionnaire: PartOfPartial<
        Http.Connection.Response.EverydayNote.QuestionnaireAdminAdmin,
        'target_roles' | 'target_school_classes' | 'target_users'
    >
): EverydayNote.AdminQuestionnaire => {
    const createdBy = {
        displayName: additionDisplayName(questionnaire.created_by).display_name,
    };
    const updatedBy = {
        displayName: additionDisplayName(questionnaire.updated_by).display_name,
    };

    const newQuestions = questionnaire.items.map(convertAdminQuestion);

    return {
        afterMessage: questionnaire.after_message,
        allowPastDateAnswer: questionnaire.allow_past_date_answer,
        canComment: questionnaire.can_comment,
        createdAt: questionnaire.created_at,
        createdBy: createdBy,
        description: questionnaire.description,
        explanation: questionnaire.explanation,
        isHolidayAnswerDisallowed: questionnaire.is_holiday_answer_disallowed,
        isLocked: questionnaire.is_locked,
        isOnceADay: questionnaire.is_once_a_day,
        isRequired: questionnaire.is_required,
        isViewedHome: questionnaire.is_viewed_home,
        items: newQuestions,
        name: questionnaire.name,
        publishEndAt: questionnaire.publish_end_at,
        publishFrom: questionnaire.publish_from,
        publishStartAt: questionnaire.publish_start_at,
        recordableDayOfWeeks: questionnaire.day_of_weeks,
        recordableTimeEnd: questionnaire.end_time,
        recordableTimeStart: questionnaire.start_time,
        statusPeriod: questionnaire.status_period,
        targetRoles: questionnaire.target_roles ?? [],
        targetSchoolClasses: questionnaire.target_school_classes ?? [],
        targetUsers: questionnaire.target_users ?? [],
        type: questionnaire.type,
        updatedAt: questionnaire.updated_at,
        updatedBy: updatedBy,
        uuid: questionnaire.uuid,
        files: questionnaire.files
    };
};
